import { useState } from "react";
import Dropdown from "components/Dropdown/Dropdown";
import { CheckIcon, ICON_STROKE, TagIcon } from "shared/icons";
import { TAG_COLORS, UNKNOWN_TAG_COLOR } from "shared/tagColors";

export interface TagSelectProps {
  tags: Tag[];
  possibleTags: Tag[];
  songName: string;
  /** May return the save's promise; the tag shows its new state until then. */
  onAdd: (tag: Tag) => unknown;
  onRemove: (tag: Tag) => unknown;
}

const swatch = (tag: Tag) => (TAG_COLORS[tag] ?? UNKNOWN_TAG_COLOR).swatch;
const titleCase = (tag: string) => tag.charAt(0).toUpperCase() + tag.slice(1);

/**
 * A track's colour tags as a cluster of dots, with a menu to toggle them.
 *
 * Tags were previously only visible, and only editable, from a song's detail
 * page. The menu stays open while toggling, since a track can carry several.
 */
export function TagSelect(props: TagSelectProps): JSX.Element {
  // The requested on/off state for each tag whose save is still in flight.
  const [pending, setPending] = useState<Partial<Record<Tag, boolean>>>({});
  const isOn = (tag: Tag) => pending[tag] ?? props.tags.includes(tag);
  const active = props.possibleTags.filter(isOn);

  function toggle(tag: Tag): void {
    const next = !isOn(tag);
    setPending((p) => ({ ...p, [tag]: next }));
    // `void`: deliberately not awaited, so quick toggles aren't queued.
    void Promise.resolve(next ? props.onAdd(tag) : props.onRemove(tag)).finally(
      () =>
        setPending((p) => {
          // A later toggle of the same tag owns its pending state now.
          if (p[tag] !== next) {
            return p;
          }
          const rest = { ...p };
          delete rest[tag];
          return rest;
        })
    );
  }

  return (
    <Dropdown
      label={
        active.length > 0
          ? `Tags for ${props.songName}: ${active.join(", ")}`
          : `Add tags to ${props.songName}`
      }
      menuLabel="Tags"
      busy={Object.keys(pending).length > 0}
      triggerClassName="inline-flex items-center justify-center h-7 w-14 rounded-pill border border-rule-strong bg-raised hover:border-muted transition-colors"
      trigger={
        active.length > 0 ? (
          // Overlapping dots, each ringed in the pill's colour so they stay
          // distinct: all six fit in the pill without widening the column.
          <span className="flex items-center pl-[3px]">
            {active.map((tag) => (
              <span
                key={tag}
                className="w-2.5 h-2.5 -ml-[3px] rounded-full"
                style={{
                  backgroundColor: swatch(tag),
                  boxShadow: "0 0 0 2px var(--raised)",
                }}
              ></span>
            ))}
          </span>
        ) : (
          <TagIcon
            size={14}
            strokeWidth={ICON_STROKE}
            className="text-muted"
            aria-hidden="true"
          />
        )
      }
    >
      {() =>
        props.possibleTags.map((tag) => {
          const checked = isOn(tag);

          return (
            <button
              key={tag}
              type="button"
              role="menuitemcheckbox"
              aria-checked={checked}
              className="menu-item"
              onClick={() => toggle(tag)}
            >
              <span
                className="w-3 h-3 rounded-full flex-none"
                style={{ backgroundColor: swatch(tag) }}
                aria-hidden="true"
              ></span>
              <span className="flex-1">{titleCase(tag)}</span>
              {checked && (
                <CheckIcon
                  size={15}
                  strokeWidth={ICON_STROKE}
                  className="flex-none text-ink-2"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })
      }
    </Dropdown>
  );
}

export default TagSelect;
